import React, { useState, useCallback, useMemo, useEffect } from "react";
import { MainAppStoreProvider, useMainAppStore } from "@rapid/adapters/MainAppStoreAdapter";
import { StoreProvider } from "@scripts/../state/StoreContext";
import { ProjectSelectorProvider } from "@scripts/react/hooks/useProjectSelector";
import { UpgradeModal } from "@scripts/react/components/UpgradeModal/UpgradeModal";
import { UpgradeModalContext, UpgradeModalContextValue } from "./UpgradeModalContext";
import { useProjectSelector } from "@rapid/contexts/ProjectSelectorContext";

interface UpgradeModalProviderProps {
    children: React.ReactNode;
}

/**
 * Inner component that renders the modal within the store adapter context
 * This is separated to avoid conditional hook calls in the main provider
 */
const UpgradeModalRenderer: React.FC<{ isOpen: boolean; fsrc: string; closeModal: () => void }> = ({
    isOpen,
    fsrc,
    closeModal
}) => {
    const adaptedStore = useMainAppStore();
    const projectSelector = useProjectSelector();

    if (!isOpen) return null;

    return (
        <ProjectSelectorProvider value={projectSelector}>
            <StoreProvider store={adaptedStore}>
                <UpgradeModal
                    isModalOpen={isOpen}
                    closeModal={closeModal}
                    fsrc={fsrc}
                    successUrl={`${window.location.origin}?checkout=success`}
                />
            </StoreProvider>
        </ProjectSelectorProvider>
    );
};

/**
 * Provider that manages the global upgrade modal state
 * This allows any component to open the upgrade modal by calling openUpgradeModal()
 * without needing to import and manage the UpgradeModal component itself
 */
export const UpgradeModalProvider: React.FC<UpgradeModalProviderProps> = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [fsrc, setFsrc] = useState<string>("rapid");

    const openUpgradeModal = useCallback((source: string = "rapid") => {
        setFsrc(source);
        setIsOpen(true);
    }, []);

    const closeUpgradeModal = useCallback(() => {
        setIsOpen(false);
    }, []);

    // Listen for messages from Stripe checkout completion
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            // Verify the message is from the same origin for security
            if (event.origin !== window.location.origin) {
                return;
            }

            // Check if it's a checkout completion message
            if (event.data?.type === "stripe-checkout-complete") {
                closeUpgradeModal();
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [closeUpgradeModal]);

    // Memoize context value to prevent unnecessary re-renders
    const contextValue = useMemo<UpgradeModalContextValue>(
        () => ({ openUpgradeModal, closeUpgradeModal, isUpgradeModalOpen: isOpen }),
        [openUpgradeModal, closeUpgradeModal, isOpen]
    );

    return (
        <UpgradeModalContext.Provider value={contextValue}>
            {children}

            {/* Render the modal at the root level - always wrapped in store provider to avoid conditional hooks */}
            <MainAppStoreProvider>
                <UpgradeModalRenderer isOpen={isOpen} fsrc={fsrc} closeModal={closeUpgradeModal} />
            </MainAppStoreProvider>
        </UpgradeModalContext.Provider>
    );
};
